import type { GreetingChange, GreetingPlaying, GreetingView } from '../api/client';

/**
 * A voicemail greeting typed in Stitch (1.12.33): what the panel says and offers, on My phone, a
 * person's page and Voicemail boxes. The phone system speaks the words; "Listen" gives a rough
 * preview with the browser's own voice.
 */

/** Which greeting callers hear, in words. */
export function playingLabel(p: GreetingPlaying, own: boolean): string {
  switch (p.kind) {
    case 'standard':
      return 'The standard greeting';
    case 'typed':
      return own ? 'Your typed greeting' : 'The typed greeting';
    case 'recorded':
      return `Recorded greeting ${p.slot}`;
    default:
      return 'A greeting set on the phone system';
  }
}

/** "42 / 300", and whether it's too long. */
export function greetingCounter(text: string, max: number): { label: string; over: boolean; left: number } {
  const n = tidy(text).length;
  return { label: `${n} / ${max}`, over: n > max, left: max - n };
}

/** The greetings that can play instead, the one playing now marked. */
export function greetingChoices(view: GreetingView, own: boolean): { key: string; label: string; change: GreetingChange; playing: boolean }[] {
  const p = view.playing;
  const out: { key: string; label: string; change: GreetingChange; playing: boolean }[] = [];
  if (view.typed) out.push({ key: 'typed', label: own ? 'Your typed greeting' : 'The typed greeting', change: { use: 'typed' }, playing: p.kind === 'typed' });
  for (const slot of view.recorded) out.push({ key: `rec-${slot}`, label: `Recorded greeting ${slot}`, change: { use: 'recorded', slot }, playing: p.kind === 'recorded' && p.slot === slot });
  out.push({ key: 'standard', label: 'The standard greeting', change: { use: 'standard' }, playing: p.kind === 'standard' });
  return out;
}

/** Whether these words can be saved now, and if not, why (in plain words). */
export function canSaveGreeting(view: GreetingView, draft: string): { ok: true } | { ok: false; why: string; same?: true } {
  const words = tidy(draft);
  if (!view.canChange) return { ok: false, why: 'Greetings can’t be changed here right now.' };
  if (!words) return { ok: false, why: 'Type the words callers should hear.' };
  if (words.length > view.maxLength) return { ok: false, why: `Keep it to ${view.maxLength} characters: a few sentences.` };
  if (!view.slotForTyped) return { ok: false, why: 'Every personal greeting holds a recording, and Stitch never records over one. Delete one from voicemail on the phone first.' };
  // Nothing to save (just saved, say): no need to say so beside the button.
  if (view.playing.kind === 'typed' && view.playing.text === words) return { ok: false, why: 'That’s the greeting callers hear now.', same: true };
  return { ok: true };
}

/** What a save (or a switch) did, in words. */
export function greetingOutcome(r: { status: string; message?: string }, change: GreetingChange, own: boolean): { tone: 'ok' | 'neutral' | 'crit'; text: string } {
  if (r.status === 'unchanged') return { tone: 'neutral', text: r.message ?? 'That’s the greeting already.' };
  if (r.status !== 'applied') return { tone: 'crit', text: r.message ?? 'That didn’t work. Nothing changed; try again.' };
  if (change.use === 'typed' && change.text !== undefined) return { tone: 'ok', text: `Saved. Callers hear ${own ? 'your' : 'the'} typed greeting now.` };
  if (change.use === 'typed') return { tone: 'ok', text: `Callers hear ${own ? 'your' : 'the'} typed greeting now.` };
  if (change.use === 'recorded') return { tone: 'ok', text: `Callers hear recorded greeting ${change.slot} now.` };
  return { tone: 'ok', text: 'Callers hear the standard greeting now.' };
}

// --- the preview: the browser's own voice ----------------------------------------------------------

/** The parts of the browser's speech synthesis the preview uses (window.speechSynthesis). */
export interface SpeechLike {
  speak(u: SpeechSynthesisUtterance): void;
  cancel(): void;
}
export type UtteranceLike = new (text?: string) => SpeechSynthesisUtterance;

/** The browser's voice, where it has one (not in every browser, nor in some private windows). */
export function browserVoice(): { synth: SpeechLike; Utterance: UtteranceLike } | null {
  if (typeof window === 'undefined' || !('speechSynthesis' in window) || typeof window.SpeechSynthesisUtterance !== 'function') return null;
  return { synth: window.speechSynthesis, Utterance: window.SpeechSynthesisUtterance };
}

/**
 * Reads the words with the browser's voice, in their voicemail's language. `done` is called when it
 * ends (or fails). Returns a stop function. A rough preview only: the phone system has its own voice.
 */
export function previewGreeting(text: string, lang: string, voice: { synth: SpeechLike; Utterance: UtteranceLike }, done: () => void): () => void {
  const { synth, Utterance } = voice;
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    done();
  };
  try {
    // Anything still being read (an earlier Listen) stops first.
    synth.cancel();
    const u = new Utterance(tidy(text));
    u.lang = lang || 'en';
    u.rate = 1;
    u.onend = finish;
    u.onerror = finish;
    synth.speak(u);
  } catch {
    finish();
  }
  return () => {
    try {
      synth.cancel();
    } catch {
      // Nothing to stop.
    }
    finish();
  };
}

function tidy(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}
